import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/db.css";
import "../../styles/results.css";
import "../../styles/libraryTable.css";
import { Row, Col, Tooltip, Tag, Dropdown, Button, message } from "antd";
import {
  DownOutlined,
  CheckOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import PageHeader from "../../components/generic/PageHeader";
import ResultTableComponent from "../../components/table/ResultTableComponent";
import { getAllResultSummary } from "../../service/result.service";
import { result_summary_column } from "../../constants/Table Columns";
import {
  groupResultSummary,
  isResultSummaryGrouped,
  sortResultSummaryRows,
  RESULT_SUMMARY_GROUP_BY,
  RESULT_SUMMARY_GROUP_OPTIONS,
} from "../../helper/resultSummaryGrouping";

function wrapColumnsForGroupedTable(columns, groupBy) {
  return columns.map((col) => {
    const origRender = col.render;
    const origOnFilter = col.onFilter;

    if (col.key === "id") {
      return {
        ...col,
        render: (text, record) => {
          if (record.isGroupHeader) {
            return (
              <span className="result-summary-group-title">
                {record.groupLabel}
              </span>
            );
          }
          return origRender ? origRender(text, record) : <a>{text}</a>;
        },
      };
    }

    if (col.key === "subCategory") {
      return {
        ...col,
        // Delegates to the column's own render instead of re-implementing it. It used
        // to call labelForSubCategory directly, which meant the Type pill appeared
        // ungrouped and silently vanished the moment a grouping was applied.
        render: (text, record) =>
          record.isGroupHeader ? null : origRender(text, record),
        onFilter: (value, record) =>
          !record.isGroupHeader && origOnFilter(value, record),
      };
    }

    if (col.key === "suiteName") {
      return {
        ...col,
        render: (text, record) => {
          if (record.isGroupHeader) {
            const count = record.children?.length ?? 0;
            return (
              <span className="result-summary-group-meta">
                {count} run{count === 1 ? "" : "s"}
              </span>
            );
          }
          return origRender(text, record);
        },
        onFilter: (value, record) =>
          !record.isGroupHeader && origOnFilter?.(value, record),
      };
    }

    if (col.key === "testResult") {
      return {
        ...col,
        render: (text, record) => {
          if (record.isGroupHeader) {
            if (groupBy === RESULT_SUMMARY_GROUP_BY.STATUS) {
              const count = record.children?.length ?? 0;
              return (
                <span className="result-summary-group-meta">
                  {count} run{count === 1 ? "" : "s"}
                </span>
              );
            }
            const children = record.children || [];
            const pass = children.filter((r) => r.testResult === "Pass").length;
            const fail = children.filter((r) => r.testResult === "Fail").length;
            return (
              <span className="result-summary-group-stats">
                {pass > 0 && <Tag color="#29b160">{pass} Passed</Tag>}
                {fail > 0 && <Tag color="#ce0000">{fail} Failed</Tag>}
                {pass === 0 && fail === 0 && (
                  <span className="result-summary-group-meta">—</span>
                )}
              </span>
            );
          }
          return origRender(text, record);
        },
        onFilter: (value, record) =>
          !record.isGroupHeader && origOnFilter(value, record),
      };
    }

    if (col.key === "user") {
      return {
        ...col,
        render: (text, record) => (record.isGroupHeader ? null : text),
        onFilter: (value, record) =>
          !record.isGroupHeader && origOnFilter?.(value, record),
      };
    }

    if (
      col.key === "runStart" ||
      col.key === "runEnd" ||
      col.key === "duration"
    ) {
      return {
        ...col,
        // Group headers carry no timings of their own. Non-header rows go through the
        // column's own render so Run Start stays formatted and Duration stays computed.
        render: (text, record) =>
          record.isGroupHeader
            ? null
            : origRender
              ? origRender(text, record)
              : text,
        sorter: col.sorter
          ? (a, b) => {
              if (a.isGroupHeader || b.isGroupHeader) return 0;
              return col.sorter(a, b);
            }
          : undefined,
      };
    }

    if (col.key === "action") {
      return col;
    }

    return {
      ...col,
      render: origRender
        ? (text, record) =>
            record.isGroupHeader ? null : origRender(text, record)
        : (text, record) => (record.isGroupHeader ? null : text),
    };
  });
}

function Results(props) {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = React.useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [groupBy, setGroupBy] = useState(RESULT_SUMMARY_GROUP_BY.NONE);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);

  useEffect(() => {
    getAllResultSummary()
      .then((res) => {
        const filteredData = res.data.filter(
          (item) => !item.parentResultSummaryKey,
        );
        setSummaryData(filteredData);
      })
      .catch((error) => {
        message.error("Failed to fetch result summary data");
      })
      .finally(() => {
        setTableLoading(false);
      });
  }, []);

  const clickTableRow = (key, record) => {
    if (record?.subCategory === "UI Test") {
      navigate("/ui-automation/results", {
        state: { resultSummaryKey: key, record },
      });
    } else if (key.includes("Master Suite")) {
      navigate("/results/mastersuite", { state: { key: key } });
    } else if (record?.category === "Process Automation") {
      navigate("/results/process", { state: { key: key } });
    } else {
      navigate("/results/details", { state: { key: key } });
    }
  };

  const flatData = useMemo(
    () => summaryData.filter((item) => item.jobType !== "MasterSuite-Task"),
    [summaryData],
  );

  const isGrouped = isResultSummaryGrouped(groupBy);

  const tableData = useMemo(() => {
    if (!isGrouped) return sortResultSummaryRows(flatData);
    return groupResultSummary(flatData, groupBy);
  }, [flatData, groupBy, isGrouped]);

  const tableColumns = useMemo(
    () =>
      isGrouped
        ? wrapColumnsForGroupedTable(result_summary_column, groupBy)
        : result_summary_column,
    [groupBy, isGrouped],
  );

  const groupableOptions = RESULT_SUMMARY_GROUP_OPTIONS.filter(
    (opt) => opt.value !== RESULT_SUMMARY_GROUP_BY.NONE,
  );
  const currentGroupLabel = RESULT_SUMMARY_GROUP_OPTIONS.find(
    (opt) => opt.value === groupBy,
  )?.label;

  const selectGroup = (value) => {
    setGroupBy(value);
    setGroupMenuOpen(false);
  };

  const groupDropdown = (
    <div className="result-group-panel">
      <div className="result-group-panel-header">
        <span>Group by</span>
        <button
          type="button"
          className="result-group-clear"
          onClick={() => selectGroup(RESULT_SUMMARY_GROUP_BY.NONE)}
        >
          Clear
        </button>
      </div>
      {groupableOptions.map((opt) => (
        <button
          type="button"
          key={opt.value}
          className={`result-group-option${
            groupBy === opt.value ? " result-group-option--active" : ""
          }`}
          onClick={() => selectGroup(opt.value)}
        >
          <span>{opt.label}</span>
          {groupBy === opt.value && (
            <CheckOutlined className="result-group-option-check" />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="db-container">
      <PageHeader
        icon={<BarChartOutlined />}
        title="Result Dashboard"
        tooltip="Use Group to organize results, or None for a flat list."
      />
      <Row className="table-container">
        <Col span={24}>
          <div className="result-summary-toolbar">
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              open={groupMenuOpen}
              onOpenChange={setGroupMenuOpen}
              dropdownRender={() => groupDropdown}
            >
              <Button
                className={`result-group-btn${
                  isGrouped ? " result-group-btn--active" : ""
                }`}
              >
                {isGrouped ? `Group: ${currentGroupLabel}` : "Group"}
                <DownOutlined className="result-group-btn-caret" />
              </Button>
            </Dropdown>
          </div>
          <ResultTableComponent
            columns={tableColumns}
            data={tableData}
            clickTableRow={clickTableRow}
            loading={tableLoading}
            grouped={isGrouped}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Results;
